import { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, BookOpen, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import type { LearningModule } from '../services/contentService';

interface ModuleReaderProps {
  module: LearningModule;
  onClose: () => void;
  onToggleComplete?: (isCompleted: boolean) => void;
  isCompleted?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export default function ModuleReader({
  module,
  onClose,
  onToggleComplete,
  isCompleted = false,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
}: ModuleReaderProps) {
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    loadContent();
  }, [module.filepath]);

  useEffect(() => {
    const handleScroll = () => {
      const article = document.getElementById('module-content');
      if (!article) return;

      const scrollTop = article.scrollTop;
      const scrollHeight = article.scrollHeight - article.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      setScrollProgress(Math.min(progress, 100));

      // Auto-mark as complete when scrolled to 90%
      if (progress > 90 && !isCompleted) {
        onToggleComplete?.(true);
      }
    };

    const article = document.getElementById('module-content');
    article?.addEventListener('scroll', handleScroll);
    return () => article?.removeEventListener('scroll', handleScroll);
  }, [isCompleted]);

  const loadContent = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(module.filepath);
      if (!response.ok) throw new Error('Failed to load module');
      const text = await response.text();
      setContent(text);
    } catch (error) {
      console.error('Error loading module:', error);
      setContent('# Error Loading Module\n\nFailed to load the module content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    onToggleComplete?.(!isCompleted);
  };

  // Custom components for ReactMarkdown
  const markdownComponents: Components = {
    a: ({ node, href, children, ...props }) => {
      const isExternal = href?.startsWith('http://') || href?.startsWith('https://');

      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline decoration-blue-400/50 hover:decoration-blue-300 transition-colors"
          {...props}
        >
          {children}
          {isExternal && (
            <ExternalLink className="w-3.5 h-3.5 inline-block flex-shrink-0" />
          )}
        </a>
      );
    },
  };

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgb(23 37 82 / 98%)' }}>
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-neutral-800 z-50">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-neutral-900/80 backdrop-blur-md border-b border-neutral-800 z-40 pt-1">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5 text-neutral-400" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-400">
                  Module {module.number}
                </span>
                <span className="text-xs text-neutral-500">•</span>
                <span className="text-xs text-neutral-400">{module.level}</span>
              </div>
              <h1 className="text-sm font-semibold text-white">{module.title}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-neutral-400">
              <Clock className="w-4 h-4" />
              {module.duration}
            </div>

            <button
              onClick={handleComplete}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isCompleted
                  ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Completed
                </>
              ) : (
                'Mark Complete'
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main
        id="module-content"
        className="fixed inset-0 top-[73px] bottom-20 overflow-y-auto"
      >
        <article className="max-w-3xl mx-auto px-6 py-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center gap-3 text-neutral-400">
                <BookOpen className="w-6 h-6 animate-pulse" />
                <span>Loading module...</span>
              </div>
            </div>
          ) : (
            <div className="prose prose-invert prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-white
              prose-h1:text-4xl prose-h1:mb-8 prose-h1:pb-4 prose-h1:border-b prose-h1:border-neutral-800
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-blue-400
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-white
              prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3 prose-h4:text-neutral-300
              prose-p:text-neutral-300 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-white prose-strong:font-semibold
              prose-ul:text-neutral-300 prose-ul:my-6
              prose-ol:text-neutral-300 prose-ol:my-6
              prose-li:my-2 prose-li:leading-relaxed
              prose-code:text-blue-300 prose-code:bg-neutral-800 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
              prose-pre:bg-neutral-800 prose-pre:border prose-pre:border-neutral-700 prose-pre:rounded-xl
              prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-neutral-400
              prose-hr:border-neutral-800 prose-hr:my-8
              prose-table:text-neutral-300
              prose-th:text-white prose-th:bg-neutral-800
              prose-td:border-neutral-700
            ">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </article>
      </main>

      {/* Footer Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-neutral-900/80 backdrop-blur-md border-t border-neutral-800 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={onPrevious}
            disabled={!hasPrevious}
            className="flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous Module
          </button>

          <div className="text-xs text-neutral-500">
            {Math.round(scrollProgress)}% complete
          </div>

          <button
            onClick={onNext}
            disabled={!hasNext}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            Next Module
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </footer>
    </div>
  );
}
